import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import BrightnessAutoOutlinedIcon from "@mui/icons-material/BrightnessAutoOutlined";
import {
	flipThemeLightDark,
	getThemePreference,
	isDarkBrandingAvailable,
	setThemePreference,
} from "../styles/branding";
import { useBrandAppearance } from "../hooks/useBrandAppearance";

export interface BrandingThemeToggleProps {
	/** Light icons on dark bars (e.g. navbar); dark icons on light backgrounds */
	variant?: "onDark" | "onLight";
}

/**
 * Sun / moon (and “match device”) controls. Only rendered when the hotel has
 * dark branding configured in chain-info.
 */
const BrandingThemeToggle: React.FC<BrandingThemeToggleProps> = ({ variant = "onLight" }) => {
	const { isDark: effectiveDark } = useBrandAppearance();
	if (!isDarkBrandingAvailable()) return null;

	const pref = getThemePreference();
	const onDark = variant === "onDark";
	const active = onDark ? "#fff" : "primary.main";
	const muted = onDark ? "rgba(255,255,255,0.65)" : "action.active";

	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0,
			}}
		>
			<Tooltip title="Match device theme" enterDelay={300}>
				<IconButton
					type="button"
					size="small"
					aria-label="Use device theme"
					onClick={() => setThemePreference("system")}
					sx={{
						color: pref === "system" ? active : muted,
					}}
				>
					<BrightnessAutoOutlinedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip
				title={effectiveDark ? "Switch to light theme" : "Switch to dark theme"}
				enterDelay={300}
			>
				<IconButton
					type="button"
					size="small"
					aria-label={effectiveDark ? "Light theme" : "Dark theme"}
					onClick={() => flipThemeLightDark()}
					sx={{ color: onDark ? "#fff" : "action.active" }}
				>
					{effectiveDark ? (
						<LightModeOutlinedIcon fontSize="small" />
					) : (
						<DarkModeOutlinedIcon fontSize="small" />
					)}
				</IconButton>
			</Tooltip>
		</Box>
	);
};

export default BrandingThemeToggle;
